import type { ApiAgent } from '@/core/entities/api/ApiAgent';
import type { FeatureEnum, PropertyTypeEnum } from '@/core/entities/CRMEnums';
import type { TransactionType } from '@/core/entities/property';
import type { H3ClusterId } from '~~/core/ports/repositories';

export type ApiPropertyType = Lowercase<keyof typeof PropertyTypeEnum>;

export type ApiTransactionType = 'rent' | 'sell' | 'rent_sell';

export const PropertyPublicationStatus = {
	PUBLISHED: 'published',
	BRAND_UNPUBLISHED: 'brand_unpublished',
	PILAR_UNPUBLISHED: 'pilar_unpublished',
	NEGOTIATED: 'negotiated',
	INACTIVE: 'inactive'
} as const;

export type PropertyPublicationStatusValue =
	(typeof PropertyPublicationStatus)[keyof typeof PropertyPublicationStatus];

export interface ApiPropertyImage {
	url: string;
	watermarkUrl?: string;
	portalWatermarkUrl: string;
}

export interface ApiAd {
	title: string;
	description: string;
	transactionType: ApiTransactionType;
}

export interface ApiThumbnail {
	thumbnailUrl?: string;
}

export interface ApiClusterProperties {
	point_count: number;
	point_ids: string[] | undefined;
	h3_boundary: [number, number][];
}

export interface ApiCluster {
	geometry: {
		type: 'Point';
		id: string;
		coordinates: [number, number];
	};
	properties: ApiClusterProperties;
}

export interface ApiPropertyDuplicationGroup {
	_id: string;
}

export interface ApiVideoView {
	viewId: string;
	completionRate: number;
	duration: number;
}

/**
 * Featured video item model from video-processor
 */
export interface ApiFeaturedVideoItem {
	url: string;
	thumbnail: string;
	duration: number;
	width: number;
	height: number;
	quality: string;
	processedAt: string;
	origin: string;
}

export interface ApiProperty {
	_id: string;
	region: string;
	city: string;
	state: string;
	ad: ApiAd;
	company: {
		id: string;
		name: string;
		smallLogo: string | null;
		largeLogo: string | null;
	};
	companyId: string;
	agent: ApiAgent;
	images: ApiPropertyImage[];
	area: number;
	rentPrice: number | null;
	askingPrice: number | null;
	commercialId: string;
	totalArea: number;
	suites: number;
	bedrooms: number;
	bathrooms: number;
	parkingSpots: number;
	isExclusive: boolean;
	building: string;
	launchDate: string;
	condoName: string;
	tax: number | null;
	condoFee: number | null;
	features: FeatureEnum[];
	thumbnail?: ApiThumbnail;
	propertyType: ApiPropertyType;
	publicationStatus: PropertyPublicationStatusValue;
	slug: string;
	pluscodeGroup: string;
	videos: string[];
	featuredVideos?: ApiFeaturedVideoItem[];
	canonicalCondo: {
		name: string;
		slug: string;
		constructionYear: number;
	};
	poiDistanceTime: string;
	h3Cluster?: ApiCluster;
	h3ClusterIds?: H3ClusterId[];
	/**
	 * Present when markUserWishlistsId is used; indicates the property is
	 * in the referenced user's wishlists.
	 */
	inUserWishlist?: boolean;
	duplicationGroup?: ApiPropertyDuplicationGroup;
	videoView?: ApiVideoView;
	/**
	 * Present in featured videos endpoint; indicates the video was watched by the user.
	 */
	watched?: boolean;
}

export type ApiTransactionTypeToDomain = Record<
	ApiTransactionType,
	TransactionType
>;

export const apiTransactionTypeToDomain: ApiTransactionTypeToDomain = {
	rent: 'rent',
	sell: 'sell',
	rent_sell: 'rentOrSell'
};

export interface Ad {
	title: string;
	description: string;
	transactionType: string;
}

export interface Company {
	name: string;
	logo: string;
}

export interface Thumbnail {
	thumbnailUrl: string;
	sourceUrl: string;
}
